import type { CellContext } from '@tanstack/table-core'
import type { TeamMember } from '@rundown-studio/types'
import { UserRole } from '@rundown-studio/types'
import type { PendingTeamMemberDto } from '@/entities/team'
import { TeamMemberRoles, useUpdateTeamMember } from '@/entities/team'
import type { FC } from 'react'
import { useState } from 'react'
import { UserTeamRoleSelect } from '../../user-team-role-select'
import { cn } from '@/utils'
import { useDashboardPermission, useUser } from '@/entities/user'

type PendingMemberRoleCellProps = {
  context: CellContext<TeamMember & PendingTeamMemberDto, UserRole>
  teamId: string
}

export const PendingMemberRoleCell: FC<PendingMemberRoleCellProps> = ({
  context,
  teamId,
}) => {
  const [patchingId, setPatchingId] = useState<string | undefined>()

  const isTeamAdmin = useDashboardPermission({
    role: UserRole.TEAM_ADMIN,
  })

  const { data: user } = useUser()

  const { mutate: updateTeamMember, isPending: isUpdatingTeamMember } =
    useUpdateTeamMember()

  const member = context.row.original
  const isCurrentUser = user?.email === member.email

  if (isTeamAdmin && !isCurrentUser) {
    return (
      <UserTeamRoleSelect
        value={member.role}
        onChange={(choice) => {
          setPatchingId(member.email)
          updateTeamMember(
            {
              teamId,
              uidOrEmail: member.email,
              payload: {
                role: choice,
              },
            },
            {
              onSettled() {
                setPatchingId(undefined)
              },
            },
          )
        }}
        loading={member.email === patchingId && isUpdatingTeamMember}
      />
    )
  }

  return (
    <span
      className={cn(
        'px-1 py-0.5 text-xs rounded-sm bg-gray-800/50',
        TeamMemberRoles[member.role as UserRole]?.css,
      )}
    >
      {TeamMemberRoles[member.role as UserRole]?.displayName}
    </span>
  )
}
